import { useState, useMemo } from 'react';
import type { Project } from '../../project/types/project';

interface ProjectFilters {
  status: string[];
  type: string[];
  customer: string[];
}

interface UseProjectFiltersProps {
  projects: Project[];
}

interface UseProjectFiltersReturn {
  searchQuery: string;
  filters: ProjectFilters;
  filteredProjects: Project[];
  hasActiveFilters: boolean;
  setSearchQuery: (query: string) => void;
  setFilters: (filters: ProjectFilters | ((prev: ProjectFilters) => ProjectFilters)) => void;
  resetFilters: () => void;
  uniqueCustomers: string[];
}

const DEFAULT_FILTERS: ProjectFilters = {
  status: [],
  type: [],
  customer: [],
};

export const useProjectFilters = ({ projects }: UseProjectFiltersProps): UseProjectFiltersReturn => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<ProjectFilters>(DEFAULT_FILTERS);

  const uniqueCustomers = useMemo(() => 
    Array.from(new Set(projects.map(p => p.customer).filter(Boolean))).sort(),
    [projects]
  );

  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      // Hierarchical search with priority order
      let matchesSearch = false;
      const query = searchQuery.trim().toLowerCase();
      
      if (query === '') {
        matchesSearch = true;
      } else {
        // Priority 1: Project Name (highest priority)
        if (project.projectName && project.projectName.toLowerCase().includes(query)) {
          matchesSearch = true;
        }
        // Priority 2: Project Number
        else if (project.projectNumber && typeof project.projectNumber === 'string' && project.projectNumber.toLowerCase().includes(query)) {
          matchesSearch = true;
        }
        // Priority 3: Location
        else if (project.location && project.location.toLowerCase().includes(query)) {
          matchesSearch = true;
        }
        // Priority 4: Customer
        else if (project.customer && project.customer.toLowerCase().includes(query)) {
          matchesSearch = true;
        }
        // Priority 5: Other fields (createdBy, createdFromApp, etc.)
        else if (
          (project.createdBy && project.createdBy.toLowerCase().includes(query)) ||
          (project.createdFromApp && project.createdFromApp.toLowerCase().includes(query)) ||
          (project.startDate && project.startDate.toLowerCase().includes(query)) ||
          (project.endDate && project.endDate.toLowerCase().includes(query))
        ) {
          matchesSearch = true;
        }
      }

      // Multi-selection status filtering
      const matchesStatus = filters.status.length === 0 || filters.status.includes(project.progress);
      
      // Multi-selection project type filtering logic
      let matchesType = true;
      if (filters.type.length > 0) {
        const projectTypes = project.projectTypes;
        const hasMatchingType = filters.type.some(filterType => {
          switch (filterType) {
            case 'Pipeline':
              return (typeof projectTypes?.pipeline === 'boolean' && projectTypes.pipeline === true) ||
                     (typeof projectTypes?.pipeline === 'object' && projectTypes.pipeline?.enabled === true);
            case 'Fabrication':
              return (typeof projectTypes?.fabrication === 'boolean' && projectTypes.fabrication === true) ||
                     (typeof projectTypes?.fabrication === 'object' && projectTypes.fabrication?.enabled === true);
            case 'Others':
              return (typeof projectTypes?.others === 'boolean' && projectTypes.others === true) ||
                     (typeof (projectTypes as any)?.other === 'boolean' && (projectTypes as any).other === true) ||
                     (typeof projectTypes?.others === 'object' && projectTypes.others?.enabled === true);
            default:
              return false;
          }
        });
        matchesType = hasMatchingType;
      }
      
      // Multi-selection customer filtering
      const matchesCustomer = filters.customer.length === 0 || (project.customer && filters.customer.includes(project.customer));

      return matchesSearch && matchesStatus && matchesType && matchesCustomer;
    });
  }, [projects, searchQuery, filters]);

  const hasActiveFilters = Boolean(searchQuery) || filters.status.length > 0 || filters.type.length > 0 || filters.customer.length > 0;

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchQuery('');
  };

  return {
    searchQuery,
    filters,
    filteredProjects,
    hasActiveFilters,
    setSearchQuery,
    setFilters,
    resetFilters,
    uniqueCustomers,
  };
}; 